import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import twemoji from "twemoji";

const LANG = {
  en: {
    placeholder: "Type your message...",
    send: "Send",
    reset: "↻ Reset",
    typing: "Typing...",
  },
};

const parseEmoji = (text) =>
  twemoji.parse(text, { folder: "svg", ext: ".svg" });

const getChatParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    chatboxId: params.get("chatboxId"),
    apiKey: params.get("apiKey"),
    chatboxName: params.get("chatboxName") || "Juno Assistant", // fallback if missing
    logoUrl:
      params.get("logoUrl") ||
      "https://cdn.jsdelivr.net/gh/lucide-icons/lucide/icons/bot.svg",
  };
};

const ChatBox = () => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { chatboxName, logoUrl } = getChatParams();

  const suggestions = [
    // "🤖 Can you help me write code?",
    // "🛠️ I encountered an error, can you fix it?",
  ];

  const sendMessage = async (text = input) => {
    if (!text.trim() || loading) return;
    const { chatboxId, apiKey } = getChatParams();

    if (!chatboxId || !apiKey) {
      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          content: "⚠️ Missing chatboxId or apiKey in the URL.",
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      return;
    }

    const timestamp = new Date().toLocaleTimeString();
    const userMessage = { role: "user", content: text, timestamp };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(
        `https://api-juno.luckytech.io/chatbox/ask/${chatboxId}?apiKey=${apiKey}`,
        {
          message: text,
        }
      );

      const fullReply = res.data.reply;
      let currentText = "";
      const newMsg = {
        role: "ai",
        content: "",
        timestamp: new Date().toLocaleTimeString(),
      };
      setMessages((prev) => [...prev, newMsg]);

      for (let i = 0; i < fullReply.length; i++) {
        currentText += fullReply[i];
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...newMsg,
            content: currentText,
          };
          return updated;
        });
        await new Promise((r) => setTimeout(r, 10));
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          content: "⚠️ Unable to connect. Please check your network.",
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const resetChat = () => setMessages([]);

  return (
    <div
      style={{
        width: 360,
        height: 450,
        border: "1px solid #ddd",
        borderRadius: "12px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
        background: "#fff",
        color: "#222",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#111",
          color: "#fff",
          padding: "12px 20px",
          fontSize: "16px",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src={logoUrl}
            alt="Bot"
            style={{ width: 20, height: 20, filter: "invert(1)" }}
          />
          {chatboxName}
        </div>
        <button
          onClick={resetChat}
          style={{
            background: "transparent",
            border: "none",
            color: "#ccc",
            cursor: "pointer",
          }}
        >
          {LANG.en.reset}
        </button>
      </div>

      {/* Suggestions */}
      <div
        style={{
          padding: "10px 16px",
          borderBottom: "1px solid #eee",
          background: "#fafafa",
        }}
      >
        {suggestions.map((s, i) => (
          <button
            key={i}
            onClick={() => sendMessage(s)}
            disabled={loading}
            style={{
              margin: "4px",
              padding: "6px 12px",
              background: "#eee",
              border: "none",
              borderRadius: "20px",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "12px",
              opacity: loading ? 0.6 : 1,
            }}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "12px",
          background: "#f9f9f9",
        }}
      >
        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              marginBottom: "10px",
              flexDirection: "column",
              alignItems: msg.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            <div style={{ display: "flex" }}>
              {msg.role === "ai" && (
                <img
                  src={logoUrl ? logoUrl : "/favicon.ico"}
                  alt="Bot Avatar"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    marginRight: 8,
                    background: "#eee",
                    objectFit: "cover",
                  }}
                />
              )}
              <div
                style={{
                  background: msg.role === "user" ? "#e6e6e6" : "#fff",
                  padding: "10px 14px",
                  borderRadius: "12px",
                  maxWidth: "75%",
                  fontSize: "14px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
                dangerouslySetInnerHTML={{ __html: parseEmoji(msg.content) }}
              ></div>
            </div>
            <div style={{ fontSize: "10px", color: "#999", marginTop: 4 }}>
              {msg.timestamp}
            </div>
          </div>
        ))}
        {loading && (
          <p
            style={{
              color: "#666",
              fontStyle: "italic",
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span>{LANG.en.typing}</span>
            <span className="dot-flash">.</span>
            <span className="dot-flash" style={{ animationDelay: "0.2s" }}>
              .
            </span>
            <span className="dot-flash" style={{ animationDelay: "0.4s" }}>
              .
            </span>
          </p>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        style={{
          display: "flex",
          borderTop: "1px solid #eee",
          padding: "10px",
          background: "#fff",
        }}
      >
        <textarea
          id="chat-input"
          rows={1}
          disabled={loading}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={LANG.en.placeholder}
          style={{
            flex: 1,
            padding: "8px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            fontSize: "14px",
            color: "#222",
            resize: "none",
          }}
        />
        <button
          onClick={() => sendMessage()}
          disabled={loading}
          style={{
            marginLeft: "10px",
            padding: "8px 16px",
            background: loading ? "#aaa" : "#111",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: loading ? "not-allowed" : "pointer",
            fontWeight: "bold",
          }}
        >
          {loading ? "..." : LANG.en.send}
        </button>
      </div>

      {/* Footer */}
      <div
        style={{
          textAlign: "center",
          fontSize: "11px",
          color: "#888",
          padding: "6px 0",
          background: "#fafafa",
          borderTop: "1px solid #eee",
        }}
      >
        Powered by{" "}
        <a
          href="https://luckytech.io"
          target="_blank"
          style={{ color: "#000" }}
        >
          Luckytech
        </a>
      </div>
    </div>
  );
};

export default ChatBox;
